import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DoneAll as DoneAllIcon,
  TableChart as TableIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  PriceChange as PriceChangeIcon,
  NotificationsActive as PriceAlertsIcon,
  Receipt as ReceiptIcon,
  EuroSymbol as EuroSymbolIcon,
  Storefront as StorefrontIcon,
  SupportAgent as SupportAgentIcon,
  Folder as FolderIcon,
  Inventory as InventoryIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Bed as BedIcon,
  MenuBook as MenuBookIcon,
  Adb as AdbIcon,
  QueryStats as QueryStatsIcon,
  Timeline as TimelineIcon,
  Warehouse as WarehouseIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";
import { logout } from "../../store/slices/authSlice";
import MDBox from "../../components/MDBox";
import { getCurrentUser } from "../../services/auth";
import { ThemeToggleButton } from "../../components/ThemeToggle";

const drawerWidth = 240;

const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [userLetter, setUserLetter] = useState("U");

  useEffect(() => {
    async function fetchUser() {
      const user = await getCurrentUser();
      const letter = user?.username
        ? user.username[0].toUpperCase()
        : user?.name
          ? user.name[0].toUpperCase()
          : user?.email
            ? user.email[0].toUpperCase()
            : "U";
      setUserLetter(letter);
    }
    fetchUser();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout());
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },

    {
      text: "File Manager",
      icon: <FolderIcon />,
      path:
        import.meta.env.VITE_FILEBROWSER_URL ||
        "https://file-manager.lage-beauty.de/login",
      external: true,
    },

    //{ text: "Profile", icon: <PersonIcon />, path: "/profile" },
    {
      text: "Statistics",
      icon: <TableIcon />,
      path: "/statistics",
    },
    {
      text: "Analysis",
      icon: <QueryStatsIcon />,
      path: "/analysis",
    },
    {
      text: "Forecast",
      icon: <TimelineIcon />,
      path: "/forecast",
    },
    {
      text: "Multi Repricer",
      icon: <PriceChangeIcon />,
      path: "/multi-repricer",
    },
    {
      text: "Invoices",
      icon: <ReceiptIcon />,
      path: "/invoices",
    },
    {
      text: "JTL Services",
      icon: <DoneAllIcon />,
      path: "/inbound-checker",
    },
    {
      text: "Plenty",
      icon: <StorefrontIcon />,
      path: "/plenty",
    },
    {
      text: "Wholesalers",
      icon: <WarehouseIcon />,
      path: "/wholesalers",
    },
    {
      text: "Penner/Renner Management",
      icon: <BedIcon />,
      path: "/penner-management",
    },
    {
      text: "Price Alerts",
      icon: <PriceAlertsIcon />,
      path: "/price-alerts",
    },
    {
      text: "Prepbusiness",
      icon: <ArrowForwardIosIcon />,
      path: "/prepbusiness",
    },
    {
      text: "Tickets",
      icon: <SupportAgentIcon />,
      path: "/tickets",
    },
    {
      text: "Debug",
      icon: <AdbIcon />,
      path: "/debug",
    },

    //{ text: "Notifications", icon: <NotificationsIcon />, path: "/notifications" },
  ];

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/dashboard":
        return "Pages / Dashboard";
      case "/settings":
        return "Pages / Settings";
      case "/multi-repricer":
        return "Services / Multi Repricer";
      case "/analysis":
        return "Services / Analysis";
      case "/forecast":
        return "Services / Forecast";
      case "/invoices":
        return "Services / Invoices";
      case "/inbound-checker":
        return "Services / Inbound Checker";
      case "/plenty":
        return "Services / Plenty";
      case "/wholesalers":
        return "Services / Wholesalers";
      case "/fba":
        return "Services / FBA";
      case "/penner-management":
        return "Services / Penner/Renner Management";
      case "/price-alerts":
      case "/rituals":
      case "/nexalia":
      case "/godel":
        return "Services / Price Alerts";
      case "/prepbusiness":
        return "Services / Prepbusiness";
      case "/tickets":
        return "Services / Tickets";

      //case "/profile":
      default:
        return "Pages / Dashboard";
    }
  };

  const isDark = theme.palette.mode === "dark";
  const navHoverBg = isDark ? "rgba(255, 255, 255, 0.06)" : "#f8f9fa";
  const navActiveBg = isDark ? "rgba(25, 118, 210, 0.18)" : "#f0f2f5";

  const drawer = (
    <Box>
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "text.primary" }}
        >
          L-AGE Dashboard
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={item.external ? "a" : Link}
              to={!item.external ? item.path : undefined}
              href={item.external ? item.path : undefined}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                mx: 2,
                borderRadius: "0.5rem",
                backgroundColor:
                  location.pathname === item.path ? navActiveBg : "transparent",
                "&:hover": {
                  backgroundColor: navHoverBg,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "2rem",
                  color:
                    location.pathname === item.path
                      ? "primary.main"
                      : "text.secondary",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: location.pathname === item.path ? 500 : 400,
                    color:
                      location.pathname === item.path
                        ? "primary.main"
                        : "text.secondary",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mt: "auto" }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={
              import.meta.env.VITE_DOCS_URL || "https://docs.lage-beauty.de/"
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: navHoverBg,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: "text.secondary",
              }}
            >
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText
              primary="Documentation"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "text.secondary",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={
              import.meta.env.VITE_GRAFANA_URL || "http://logs.lage-beauty.de"
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: navHoverBg,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: "text.secondary",
              }}
            >
              <QueryStatsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Grafana Logs"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "text.secondary",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={
              import.meta.env.VITE_SENTRY_URL ||
              "https://l-age-ecommerce-gmbh.sentry.io"
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: navHoverBg,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: "text.secondary",
              }}
            >
              <BugReportIcon />
            </ListItemIcon>
            <ListItemText
              primary="Sentry"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "text.secondary",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/settings"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor:
                location.pathname === "/settings" ? navActiveBg : "transparent",
              "&:hover": {
                backgroundColor: navHoverBg,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color:
                  location.pathname === "/settings"
                    ? "primary.main"
                    : "text.secondary",
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: location.pathname === "/settings" ? 500 : 400,
                  color:
                    location.pathname === "/settings"
                      ? "primary.main"
                      : "text.secondary",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              "&:hover": {
                backgroundColor: "#ffebee",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: "2rem", color: "#f44336" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  color: "#f44336",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth + 36}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: "none",
          borderRadius: "0.75rem",
          margin: "18px",
          backdropFilter: "saturate(200%) blur(30px)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" }, color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              color: "text.primary",
              fontWeight: "bold",
            }}
          >
            {getPageTitle()}
          </Typography>
          <ThemeToggleButton sx={{ color: "text.primary" }} />
          <IconButton onClick={() => navigate("/settings")} sx={{ ml: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#1976d2",
                cursor: "pointer",
              }}
            >
              {userLetter}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "background.paper",
              border: "none",
              boxShadow: isDark
                ? "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.35)"
                : "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: "background.default",
        }}
      >
        <Toolbar />
        <MDBox py={3}>{children}</MDBox>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
